import type { Language, ModeId, CardType, Phase, ActionResult } from "./enums";

// Single source of UI strings. Identical keys across UK/RU/EN (§7).
type Dict = {
  app_title: string;
  app_tagline: string;
  cta_continue: string;
  cta_start: string;
  cta_start_over: string;
  cta_play_again: string;
  cta_done: string;
  cta_failed: string;
  cta_skip: string;
  cta_replace: string;
  cta_close: string;
  cta_cancel: string;
  cta_confirm: string;
  cta_save: string;
  cta_delete: string;
  cta_back: string;
  cta_finish: string;
  cta_add: string;
  cta_undo: string;
  resume_session: string;
  mode_self: string;
  mode_ask: string;
  mode_shout: string;
  mode_vote_room: string;
  mode_compete: string;
  mode_chain: string;
  mode_mirror: string;
  mode_solo: string;
  reaction_title: string;
  reaction_hint: string;
  reaction_continue: string;
  reaction_auto: string;
  consequence_continue: string;
  saved_groups_title: string;
  save_group_cta: string;
  save_group_dialog_title: string;
  save_group_name_placeholder: string;
  save_group_replace: string;
  load_group_cta: string;
  delete_group_confirm: string;
  delete_group_cta: string;
  group_default_name: string;
  no_saved_groups: string;
  language_pick: string;
  modes_title: string;
  packs_title: string;
  pack_status_not_started: string;
  pack_status_in_progress: string;
  pack_status_completed: string;
  pack_cards_label: string;
  pack_arc_label: string;
  players_title: string;
  players_counter: string;
  players_placeholder: string;
  players_min_warning: string;
  session_picking: string;
  session_close_confirm_title: string;
  session_close_confirm_body: string;
  session_close_discard: string;
  age_gate_title: string;
  age_gate_body: string;
  age_gate_confirm: string;
  stats_hero_label: string;
  stats_per_player: string;
  stats_done: string;
  settings_title: string;
  settings_language: string;
  settings_reset_content: string;
  settings_reset_content_done: string;
  settings_admin: string;
  settings_about: string;
  phase_warmup: string;
  phase_engagement: string;
  phase_peak: string;
  type_question: string;
  type_task: string;
  type_mini_challenge: string;
  type_vote: string;
  type_pair: string;
  type_group: string;
  mode_family: string;
  mode_friends: string;
  mode_party: string;
  mode_alco: string;
  mode_couples: string;
  mode_family_desc: string;
  mode_friends_desc: string;
  mode_party_desc: string;
  mode_alco_desc: string;
  mode_couples_desc: string;
  timer_tap_to_start: string;
  timer_long_press_reset: string;
  no_packs: string;
  loading: string;
};

const uk: Dict = {
  app_title: "Портал Паті",
  app_tagline: "Вечірка в одному телефоні",
  cta_continue: "Продовжити",
  cta_start: "Старт",
  cta_start_over: "Почати спочатку",
  cta_play_again: "Зіграти знову",
  cta_done: "Виконано",
  cta_failed: "Не вийшло",
  cta_skip: "Пропустити",
  cta_replace: "Інша картка",
  cta_close: "Закрити",
  cta_cancel: "Скасувати",
  cta_confirm: "Підтвердити",
  cta_save: "Зберегти",
  cta_delete: "Видалити",
  cta_back: "Назад",
  cta_finish: "Готово",
  cta_add: "Додати",
  cta_undo: "Скасувати",
  resume_session: "Продовжити сесію",
  mode_self: "Розкажи сам про себе",
  mode_ask: "Постав питання помічнику",
  mode_shout: "Хто перший вигукне — той виграв",
  mode_vote_room: "Усі голосуйте пальцем",
  mode_compete: "Хто з помічників зробить краще — група судить",
  mode_chain: "По черзі — кожен додає свою частину",
  mode_mirror: "Повторюйте один одного дзеркально",
  mode_solo: "Тільки ти",
  reaction_title: "Реагуйте!",
  reaction_hint: "👏 🔥 😂 — компанія висловлюється",
  reaction_continue: "Далі",
  reaction_auto: "Авто-перехід через",
  consequence_continue: "Далі",
  saved_groups_title: "Збережені групи",
  save_group_cta: "Зберегти групу",
  save_group_dialog_title: "Назвати групу",
  save_group_name_placeholder: "Назва (наприклад «Дача 2026»)",
  save_group_replace: "Перезаписати",
  load_group_cta: "Завантажити",
  delete_group_confirm: "Видалити цю групу?",
  delete_group_cta: "Видалити",
  group_default_name: "Моя компанія",
  no_saved_groups: "Поки що немає збережених груп",
  language_pick: "Оберіть мову",
  modes_title: "Режими",
  packs_title: "Колоди",
  pack_status_not_started: "Нова",
  pack_status_in_progress: "У процесі",
  pack_status_completed: "Завершена",
  pack_cards_label: "карток у колоді",
  pack_arc_label: "Емоційна крива вечора",
  players_title: "Гравці",
  players_counter: "Гравці · {n}",
  players_placeholder: "Ім'я гравця",
  players_min_warning: "Мінімум 2 гравці",
  session_picking: "Обираємо…",
  session_close_confirm_title: "Завершити сесію?",
  session_close_confirm_body: "Прогрес не збережеться.",
  session_close_discard: "Завершити",
  age_gate_title: "18+ контент",
  age_gate_body: "Цей режим містить дорослий контент. Підтверджуєте, що всі гравці повнолітні?",
  age_gate_confirm: "Так, нам 18+",
  stats_hero_label: "Карток зіграно",
  stats_per_player: "По гравцях",
  stats_done: "Готово",
  settings_title: "Налаштування",
  settings_language: "Мова",
  settings_reset_content: "Скинути контент",
  settings_reset_content_done: "Контент перезавантажено",
  settings_admin: "Адмінка",
  settings_about: "Про застосунок",
  phase_warmup: "Розігрів",
  phase_engagement: "Залучення",
  phase_peak: "Пік",
  type_question: "Питання",
  type_task: "Завдання",
  type_mini_challenge: "Міні-челендж",
  type_vote: "Голосування",
  type_pair: "Пара",
  type_group: "Група",
  mode_family: "Сімейний",
  mode_friends: "Друзі",
  mode_party: "Вечірка",
  mode_alco: "Алко",
  mode_couples: "Парний",
  mode_family_desc: "М'які жарти й тепла атмосфера",
  mode_friends_desc: "Веселощі для близьких",
  mode_party_desc: "Висока енергія, гучна компанія",
  mode_alco_desc: "Дорослий формат, келих у руці",
  mode_couples_desc: "Лише для пар",
  timer_tap_to_start: "Тап — старт/пауза",
  timer_long_press_reset: "Утримуй — скинути",
  no_packs: "Поки що немає колод",
  loading: "Завантаження…",
};

const ru: Dict = {
  app_title: "Портал Пати",
  app_tagline: "Вечеринка в одном телефоне",
  cta_continue: "Продолжить",
  cta_start: "Старт",
  cta_start_over: "Начать заново",
  cta_play_again: "Сыграть ещё",
  cta_done: "Готово",
  cta_failed: "Не получилось",
  cta_skip: "Пропустить",
  cta_replace: "Другая карта",
  cta_close: "Закрыть",
  cta_cancel: "Отмена",
  cta_confirm: "Подтвердить",
  cta_save: "Сохранить",
  cta_delete: "Удалить",
  cta_back: "Назад",
  cta_finish: "Готово",
  cta_add: "Добавить",
  cta_undo: "Отменить",
  resume_session: "Продолжить сессию",
  mode_self: "Расскажи о себе",
  mode_ask: "Задай вопрос помощнику",
  mode_shout: "Кто первый выкрикнет — тот выиграл",
  mode_vote_room: "Все голосуйте пальцем",
  mode_compete: "Кто из помощников сделает лучше — группа судит",
  mode_chain: "По очереди — каждый добавляет своё",
  mode_mirror: "Повторяйте друг друга зеркально",
  mode_solo: "Только ты",
  reaction_title: "Реагируйте!",
  reaction_hint: "👏 🔥 😂 — компания высказывается",
  reaction_continue: "Дальше",
  reaction_auto: "Авто-переход через",
  consequence_continue: "Дальше",
  saved_groups_title: "Сохранённые группы",
  save_group_cta: "Сохранить группу",
  save_group_dialog_title: "Назвать группу",
  save_group_name_placeholder: "Название (например «Дача 2026»)",
  save_group_replace: "Перезаписать",
  load_group_cta: "Загрузить",
  delete_group_confirm: "Удалить эту группу?",
  delete_group_cta: "Удалить",
  group_default_name: "Моя компания",
  no_saved_groups: "Пока нет сохранённых групп",
  language_pick: "Выберите язык",
  modes_title: "Режимы",
  packs_title: "Колоды",
  pack_status_not_started: "Новая",
  pack_status_in_progress: "В процессе",
  pack_status_completed: "Пройдена",
  pack_cards_label: "карт в колоде",
  pack_arc_label: "Эмоциональная кривая вечера",
  players_title: "Игроки",
  players_counter: "Игроки · {n}",
  players_placeholder: "Имя игрока",
  players_min_warning: "Минимум 2 игрока",
  session_picking: "Выбираем…",
  session_close_confirm_title: "Завершить сессию?",
  session_close_confirm_body: "Прогресс не сохранится.",
  session_close_discard: "Завершить",
  age_gate_title: "18+ контент",
  age_gate_body: "Этот режим содержит взрослый контент. Подтверждаете, что все игроки совершеннолетние?",
  age_gate_confirm: "Да, нам 18+",
  stats_hero_label: "Карт сыграно",
  stats_per_player: "По игрокам",
  stats_done: "Готово",
  settings_title: "Настройки",
  settings_language: "Язык",
  settings_reset_content: "Сбросить контент",
  settings_reset_content_done: "Контент перезагружен",
  settings_admin: "Админка",
  settings_about: "О приложении",
  phase_warmup: "Разогрев",
  phase_engagement: "Вовлечение",
  phase_peak: "Пик",
  type_question: "Вопрос",
  type_task: "Задание",
  type_mini_challenge: "Мини-челлендж",
  type_vote: "Голосование",
  type_pair: "Пара",
  type_group: "Группа",
  mode_family: "Семейный",
  mode_friends: "Друзья",
  mode_party: "Вечеринка",
  mode_alco: "Алко",
  mode_couples: "Парный",
  mode_family_desc: "Мягкие шутки и тёплая атмосфера",
  mode_friends_desc: "Веселье в кругу близких",
  mode_party_desc: "Высокая энергия, шумная компания",
  mode_alco_desc: "Взрослый формат, бокал в руке",
  mode_couples_desc: "Только для пар",
  timer_tap_to_start: "Тап — старт/пауза",
  timer_long_press_reset: "Удерживай — сброс",
  no_packs: "Пока нет колод",
  loading: "Загрузка…",
};

const en: Dict = {
  app_title: "Portal Party",
  app_tagline: "Party in one phone",
  cta_continue: "Continue",
  cta_start: "Start",
  cta_start_over: "Start over",
  cta_play_again: "Play again",
  cta_done: "Done",
  cta_failed: "Failed",
  cta_skip: "Skip",
  cta_replace: "Replace",
  cta_close: "Close",
  cta_cancel: "Cancel",
  cta_confirm: "Confirm",
  cta_save: "Save",
  cta_delete: "Delete",
  cta_back: "Back",
  cta_finish: "Done",
  cta_add: "Add",
  cta_undo: "Undo",
  resume_session: "Resume session",
  mode_self: "Tell us about yourself",
  mode_ask: "Ask the helper",
  mode_shout: "First to shout wins",
  mode_vote_room: "Everyone vote with a finger",
  mode_compete: "Helpers compete — group judges",
  mode_chain: "Take turns — each adds a piece",
  mode_mirror: "Mirror each other",
  mode_solo: "Just you",
  reaction_title: "React!",
  reaction_hint: "👏 🔥 😂 — the room reacts",
  reaction_continue: "Next",
  reaction_auto: "Auto-skip in",
  consequence_continue: "Next",
  saved_groups_title: "Saved groups",
  save_group_cta: "Save group",
  save_group_dialog_title: "Name this group",
  save_group_name_placeholder: "Name (e.g. \"Cabin trip\")",
  save_group_replace: "Overwrite",
  load_group_cta: "Load",
  delete_group_confirm: "Delete this group?",
  delete_group_cta: "Delete",
  group_default_name: "My crew",
  no_saved_groups: "No saved groups yet",
  language_pick: "Choose language",
  modes_title: "Modes",
  packs_title: "Packs",
  pack_status_not_started: "New",
  pack_status_in_progress: "In progress",
  pack_status_completed: "Completed",
  pack_cards_label: "cards in pack",
  pack_arc_label: "Emotional arc of the night",
  players_title: "Players",
  players_counter: "Players · {n}",
  players_placeholder: "Player name",
  players_min_warning: "Min 2 players",
  session_picking: "Picking…",
  session_close_confirm_title: "End session?",
  session_close_confirm_body: "Progress will be lost.",
  session_close_discard: "End",
  age_gate_title: "18+ content",
  age_gate_body: "This mode contains adult content. Confirm all players are 18+.",
  age_gate_confirm: "Yes, we are 18+",
  stats_hero_label: "Cards played",
  stats_per_player: "Per player",
  stats_done: "Done",
  settings_title: "Settings",
  settings_language: "Language",
  settings_reset_content: "Reset content",
  settings_reset_content_done: "Content reloaded",
  settings_admin: "Admin",
  settings_about: "About",
  phase_warmup: "Warmup",
  phase_engagement: "Engagement",
  phase_peak: "Peak",
  type_question: "Question",
  type_task: "Task",
  type_mini_challenge: "Mini-challenge",
  type_vote: "Vote",
  type_pair: "Pair",
  type_group: "Group",
  mode_family: "Family",
  mode_friends: "Friends",
  mode_party: "Party",
  mode_alco: "Alco",
  mode_couples: "Couples",
  mode_family_desc: "Gentle jokes and warm vibes",
  mode_friends_desc: "Fun for close ones",
  mode_party_desc: "High energy, loud crowd",
  mode_alco_desc: "Adult format, drink in hand",
  mode_couples_desc: "Couples only",
  timer_tap_to_start: "Tap — start/pause",
  timer_long_press_reset: "Long-press — reset",
  no_packs: "No packs yet",
  loading: "Loading…",
};

export const STRINGS: Record<Language, Dict> = { uk, ru, en };

export type StringKey = keyof Dict;

export function t(lang: Language, key: StringKey, vars?: Record<string, string | number>): string {
  let s = STRINGS[lang][key];
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.replace(`{${k}}`, String(v));
    }
  }
  return s;
}

export function modeName(lang: Language, modeId: ModeId): string {
  const key = `mode_${modeId}` as StringKey;
  return STRINGS[lang][key];
}
export function modeDesc(lang: Language, modeId: ModeId): string {
  return STRINGS[lang][`mode_${modeId}_desc` as StringKey];
}
export function cardTypeName(lang: Language, ct: CardType): string {
  return STRINGS[lang][`type_${ct}` as StringKey];
}
export function phaseName(lang: Language, p: Phase): string {
  return STRINGS[lang][`phase_${p}` as StringKey];
}
// kept for stats labels
export function actionLabel(lang: Language, a: ActionResult): string {
  switch (a) {
    case "done":
      return t(lang, "cta_done");
    case "failed":
      return t(lang, "cta_failed");
    case "skipped":
      return t(lang, "cta_skip");
    case "replaced":
      return t(lang, "cta_replace");
  }
}
